import React, { useRef, useState, useEffect } from 'react';
import { signInWithGoogle, signOutUser } from '../firebaseClient';
import AuthModal from './AuthModal';
import { countTotals, MAX_RESUMES, MAX_COVER_LETTERS_TOTAL } from '../data/projectStore';
import './Dashboard.css';

// ── Helpers ───────────────────────────────────────────────
function formatDate(ts) {
  if (!ts) return '—';
  const diff = Date.now() - ts;
  const days = Math.floor(diff / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days < 7)  return `${days} days ago`;
  return new Date(ts).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function docSummary(project) {
  const parts = [];
  if (project.resume) parts.push('1 resume');
  const cl = project.coverLetters.length;
  if (cl > 0) parts.push(`${cl} cover letter${cl > 1 ? 's' : ''}`);
  return parts.join(' · ') || 'Empty project';
}

// ── Project type icon ─────────────────────────────────────
function TypeIcon({ project }) {
  if (project.resume && project.coverLetters.length > 0) return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <rect x="2" y="7" width="20" height="14" rx="2"/>
      <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2"/>
      <line x1="12" y1="12" x2="12" y2="16"/><line x1="10" y1="14" x2="14" y2="14"/>
    </svg>
  );
  if (project.resume) return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z"/>
      <polyline points="14 2 14 8 20 8"/>
    </svg>
  );
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
      <polyline points="22,6 12,13 2,6"/>
    </svg>
  );
}

// ── Project card ──────────────────────────────────────────
function ProjectCard({ project, onOpen, onDelete, onDuplicate }) {
  const [confirming, setConfirming] = useState(false);

  return (
    <div className="proj-card animate-in">
      <div className="proj-card-icon">
        <TypeIcon project={project} />
      </div>
      <div className="proj-card-body" onClick={() => onOpen(project)}>
        <div className="proj-card-name">{project.name}</div>
        <div className="proj-card-docs">{docSummary(project)}</div>
        <div className="proj-card-date">Edited {formatDate(project.updatedAt)}</div>
      </div>
      <div className="proj-card-actions">
        {confirming ? (
          <div className="proj-card-confirm">
            <span className="proj-card-confirm-text">Delete?</span>
            <button className="btn-proj-confirm-yes" onClick={() => onDelete(project.id)}>Yes</button>
            <button className="btn-proj-confirm-no"  onClick={() => setConfirming(false)}>No</button>
          </div>
        ) : (
          <>
            <button className="btn-proj-duplicate" onClick={() => onDuplicate(project)} title="Duplicate project">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="9" y="9" width="13" height="13" rx="2"/>
                <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
              </svg>
            </button>
            <button className="btn-proj-delete" onClick={() => setConfirming(true)} title="Delete project">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="3 6 5 6 21 6"/>
                <path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/>
                <path d="M10 11v6M14 11v6M9 6V4a1 1 0 011-1h4a1 1 0 011 1v2"/>
              </svg>
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Cover letter nudge overlay ────────────────────────────
// onLinkToResume is only passed when at least one linkable resume project exists
function CoverLetterNudge({ onProceed, onCreateResume, onLinkToResume, onCancel }) {
  return (
    <div className="nudge-overlay" onClick={onCancel}>
      <div className="nudge-modal" onClick={e => e.stopPropagation()}>
        <div className="nudge-icon">✉️</div>
        <h2 className="nudge-title">Start with a resume first?</h2>
        <p className="nudge-body">
          Our AI writes much stronger cover letters when it has your resume to reference —
          your experience, skills, and tone are already there. We recommend building or
          importing a resume before creating a cover letter.
        </p>
        <div className="nudge-actions">
          {onLinkToResume && (
            <button className="btn-nudge-resume" onClick={onLinkToResume}>
              🔗 Link to Existing Resume
            </button>
          )}
          <button className={onLinkToResume ? 'btn-nudge-proceed' : 'btn-nudge-resume'} onClick={onCreateResume}>
            Create a Resume First
          </button>
          <button className="btn-nudge-proceed" onClick={onProceed}>
            Proceed with Cover Letter Only
          </button>
        </div>
        <button className="btn-nudge-cancel" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Resume start modal ────────────────────────────────────
function ResumeModal({ onScratch, onImport, onCancel }) {
  return (
    <div className="nudge-overlay" onClick={onCancel}>
      <div className="nudge-modal" onClick={e => e.stopPropagation()}>
        <div className="nudge-icon">📄</div>
        <h2 className="nudge-title">How would you like to start?</h2>
        <p className="nudge-body">
          Build your resume step by step, or upload an existing PDF and we'll
          extract your information automatically.
        </p>
        <div className="nudge-actions">
          <button className="btn-nudge-resume" onClick={onImport}>
            ↑ Import Existing PDF
          </button>
          <button className="btn-nudge-proceed" onClick={onScratch}>
            Start from Scratch
          </button>
        </div>
        <button className="btn-nudge-cancel" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

// ── Feedback form ─────────────────────────────────────────
function FeedbackForm() {
  const [message, setMessage] = useState('');
  const [status, setStatus]   = useState(null);

  const handleSubmit = async () => {
    if (!message.trim()) return;
    setStatus('sending');
    try {
      const res = await fetch('https://formspree.io/f/xqegrvkk', {
        method:  'POST',
        headers: { 'Content-Type': 'application/json' },
        body:    JSON.stringify({ message }),
      });
      setStatus(res.ok ? 'sent' : 'error');
      if (res.ok) setMessage('');
    } catch {
      setStatus('error');
    }
    setTimeout(() => setStatus(null), 4000);
  };

  if (status === 'sent') {
    return <p className="feedback-thanks">✓ Thanks for your feedback!</p>;
  }

  return (
    <div className="feedback-form">
      <textarea
        className="feedback-textarea"
        placeholder="Share your idea or feedback…"
        value={message}
        onChange={e => setMessage(e.target.value)}
        rows={2}
      />
      <button
        className="feedback-btn"
        onClick={handleSubmit}
        disabled={status === 'sending' || !message.trim()}
      >
        {status === 'sending' ? 'Sending…' : status === 'error' ? 'Try again' : 'Send'}
      </button>
    </div>
  );
}

// ── Dashboard ─────────────────────────────────────────────
export default function Dashboard({
  projects,
  onOpenProject,
  onDeleteProject,
  onDuplicateProject,
  onNewResume,
  onNewCoverLetter,
  onNewFull,
  onImport,
  onLinkCoverLetterToResume,
  uploadStatus,
  user,
  authLoading,
  lastSaved,
  isSaving,
  onStartTour,
}) {
  const fileRef = useRef();
  const [, setTick] = useState(0);
  const [showAuth,        setShowAuth]        = useState(false);
  const [showNudge,       setShowNudge]       = useState(false);
  const [showResumeModal, setShowResumeModal] = useState(null);

  useEffect(() => {
    if (!lastSaved) return;
    const interval = setInterval(() => setTick(t => t + 1), 10000);
    return () => clearInterval(interval);
  }, [lastSaved]);

  const saveLabel = () => {
    if (!user) return null;
    if (isSaving) return '↑ Saving…';
    if (!lastSaved) return null;
    const secs = Math.floor((Date.now() - lastSaved.getTime()) / 1000);
    if (secs < 10)  return '✓ Saved';
    if (secs < 60)  return `✓ Saved ${secs}s ago`;
    return `✓ Saved ${Math.floor(secs / 60)}m ago`;
  };
  const label = saveLabel();

  const { resumes, coverLetters } = countTotals(projects);
  const atResumeLimit = resumes      >= MAX_RESUMES;
  const atCoverLimit  = coverLetters >= MAX_COVER_LETTERS_TOTAL;
  const atFullLimit   = atResumeLimit || atCoverLimit;

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.type === 'application/pdf') onImport(file);
    e.target.value = '';
  };

  return (
    <div className="dashboard">
      {/* ── Header ── */}
      <header className="dashboard-header">
        <div className="dashboard-logo">
          <img src={`${process.env.PUBLIC_URL}/logos/logo.png`} alt="Cedar Resumes" className="dashboard-logo-img" />
          <div className="dashboard-logo-text">
            <span className="dashboard-logo-name">Cedar Resumes</span>
            <span className="dashboard-logo-tagline">Build it. Love it. Send it.</span>
          </div>
        </div>
        <div className="dashboard-header-actions">
          {label && (
            <span className={`header-save-status ${isSaving ? 'saving' : 'saved'}`}>{label}</span>
          )}
          {user && (
            <div className="header-user">
              <div className="header-avatar-initials">
                {(user.displayName || user.email || '')
                  .split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase()}
              </div>
              <span className="header-user-name">{user.displayName || user.email}</span>
              <button className="btn-sign-out" onClick={signOutUser}>Sign out</button>
            </div>
          )}
        </div>
      </header>

      <main className="dashboard-main">

        {/* ── Existing projects ── */}
        {projects.length > 0 && (
          <section className="dashboard-section">
            <h2 className="dashboard-section-label">My Projects</h2>
            <div className="proj-cards-list">
              {projects.map(p => (
                <ProjectCard key={p.id} project={p} onOpen={onOpenProject} onDelete={onDeleteProject} onDuplicate={onDuplicateProject} />
              ))}
            </div>
          </section>
        )}

        {/* ── Tour section ── */}
        {onStartTour && (
          <section className="dashboard-section dash-tour-section">
            <h2 className="dashboard-section-label">New to Cedar Resumes?</h2>
            <div className="dash-tour-card">
              <div className="dash-tour-card-text">
                <p className="dash-tour-card-title">Take a guided tour</p>
                <p className="dash-tour-card-desc">See how the resume builder, cover letter editor, and project hub work together — takes about 2 minutes.</p>
              </div>
              <button className="btn-dash-tour" onClick={onStartTour}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 015.83 1c0 2-3 3-3 3"/><circle cx="12" cy="17" r="0.5" fill="currentColor"/>
                </svg>
                Take a Tour
              </button>
            </div>
          </section>
        )}

        {/* ── Start new ── */}
        <section className="dashboard-section">
          <div className="dashboard-hero">
            <h1 className="dashboard-hero-title">
              {projects.length > 0 ? 'Start Something New' : "Let's Get Started"}
            </h1>
            <p className="dashboard-hero-sub">What would you like to create?</p>
          </div>

          <div className="dashboard-new-grid">

            {!user && !authLoading && (
              <div className="dash-signup-banner">
                <div className="dash-signup-banner-text">
                  <span className="dash-signup-icon">☁️</span>
                  <div>
                    <p className="dash-signup-title">Save your work everywhere</p>
                    <p className="dash-signup-sub">Create an account or sign in to access your resumes and cover letters on any device.</p>
                  </div>
                </div>
                <button
                  className="btn-dash-signup"
                  onClick={async () => {
                    try { await signInWithGoogle(); }
                    catch (e) { if (e.code !== 'auth/cancelled-popup-request' && e.code !== 'auth/popup-closed-by-user') console.error(e); }
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
                  </svg>
                  Sign in with Google
                </button>
              </div>
            )}

            {/* Resume */}
            <div className={`dash-new-card ${atResumeLimit ? 'at-limit' : ''}`}>
              <span className="dash-new-icon resume-icon">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="4" y="2" width="16" height="20" rx="2"/>
                  <line x1="8" y1="8" x2="16" y2="8"/>
                  <line x1="8" y1="12" x2="16" y2="12"/>
                  <line x1="8" y1="16" x2="12" y2="16"/>
                </svg>
              </span>
              <span className="dash-new-title">Resume</span>
              <span className="dash-new-desc">
                {atResumeLimit
                  ? `You've reached the limit of ${MAX_RESUMES} resumes. Delete one to create another.`
                  : 'Build a polished resume from scratch or import an existing PDF.'}
              </span>
              {!atResumeLimit && (
                <div className="dash-new-btns">
                  <button className="btn-dash-primary" onClick={() => setShowResumeModal('resume')}>Get Started</button>
                </div>
              )}
            </div>

            {/* Cover Letter */}
            <div className={`dash-new-card ${atCoverLimit ? 'at-limit' : ''}`}>
              <span className="dash-new-icon letter-icon">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </span>
              <span className="dash-new-title">Cover Letter</span>
              <span className="dash-new-desc">
                {atCoverLimit
                  ? `You've reached the limit of ${MAX_COVER_LETTERS_TOTAL} cover letters.`
                  : 'AI-crafted cover letter tailored to the role you\'re applying for.'}
              </span>
              {!atCoverLimit && (
                <div className="dash-new-btns">
                  <button className="btn-dash-primary" onClick={() => setShowNudge(true)}>Get Started</button>
                </div>
              )}
            </div>

            {/* Resume + Cover Letter */}
            <div className={`dash-new-card ${atFullLimit ? 'at-limit' : ''}`}>
              <span className="dash-new-icon full-icon">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <rect x="4" y="2" width="16" height="20" rx="2"/>
                  <line x1="8" y1="8" x2="16" y2="8"/>
                  <line x1="8" y1="12" x2="16" y2="12"/>
                  <line x1="8" y1="16" x2="12" y2="16"/>
                </svg>
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
                  <polyline points="22,6 12,13 2,6"/>
                </svg>
              </span>
              <span className="dash-new-title">Resume + Cover Letter</span>
              <span className="dash-new-desc">
                {atFullLimit
                  ? 'You\'ve reached a project limit.'
                  : 'Create a resume and up to 3 cover letters together as one linked project.'}
              </span>
              {!atFullLimit && (
                <div className="dash-new-btns">
                  <button className="btn-dash-primary" onClick={() => setShowResumeModal('full')}>Get Started</button>
                </div>
              )}
            </div>

          </div>
        </section>
      </main>

      <input ref={fileRef} type="file" accept="application/pdf" style={{ display: 'none' }} onChange={handleFileChange} />

      <footer className="dashboard-footer">
        <div className="dashboard-footer-inner">
          <div className="dashboard-footer-brand">
            <div className="dashboard-footer-brand-top">
              <img src={`${process.env.PUBLIC_URL}/logos/logo.png`} alt="Cedar Resumes" className="dashboard-footer-logo" />
              <div className="dashboard-footer-brand-text">
                <span className="dashboard-footer-name">Cedar Resumes</span>
                <span className="dashboard-footer-tagline">Build it. Love it. Send it.</span>
              </div>
            </div>
            <p className="dashboard-footer-copy">© 2026 Cedar Resumes by Julien Ataya</p>
            <div className="dashboard-footer-legal">
              <a href="/privacy.html" className="dashboard-footer-legal-link">Privacy Policy</a>
              <span className="dashboard-footer-legal-sep">·</span>
              <a href="/terms.html" className="dashboard-footer-legal-link">Terms of Service</a>
            </div>
          </div>
          <div className="dashboard-footer-contact">
            <p className="dashboard-footer-prompt">Have a suggestion? We'd love to hear it!</p>
            <FeedbackForm />
          </div>
        </div>
      </footer>

      {/* Resume start modal */}
      {showResumeModal && (
        <ResumeModal
          onScratch={() => {
            const handler = showResumeModal === 'full' ? onNewFull : onNewResume;
            setShowResumeModal(null);
            handler();
          }}
          onImport={() => { setShowResumeModal(null); fileRef.current?.click(); }}
          onCancel={() => setShowResumeModal(null)}
        />
      )}

      {/* Cover letter nudge overlay */}
      {showNudge && (
        <CoverLetterNudge
          onProceed={() => { setShowNudge(false); onNewCoverLetter(); }}
          onCreateResume={() => { setShowNudge(false); onNewResume(); }}
          onLinkToResume={onLinkCoverLetterToResume
            ? () => { setShowNudge(false); onLinkCoverLetterToResume(); }
            : null}
          onCancel={() => setShowNudge(false)}
        />
      )}

      {showAuth && <AuthModal onClose={() => setShowAuth(false)} />}
    </div>
  );
}
